import { useState, useEffect } from 'react';
import { 
  query, 
  where, 
  onSnapshot, 
  serverTimestamp, 
  getDocs,
  doc,
  runTransaction,
  orderBy,
  collection,
  addDoc
} from 'firebase/firestore';
import { db, reviewsCollection } from '../lib/firebase';
import type { Review } from '../types/index';
import toast from 'react-hot-toast';

export function useReviews(extensionId: string) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!extensionId) return;

    const q = query(
      reviewsCollection,
      where('extensionId', '==', extensionId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const reviewsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Review[];
        
        setReviews(reviewsData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Error fetching reviews:', error);
        setError('Failed to load reviews');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [extensionId]);

  const addReview = async (
    userId: string,
    userDisplayName: string,
    userPhotoURL: string,
    rating: number,
    comment: string,
    userIcon: string
  ) => {
    console.log('addReview called with:', {
      extensionId,
      userId,
      userDisplayName,
      userPhotoURL,
      rating,
      comment,
      userIcon
    });

    // Validate extension ID
    if (!extensionId?.trim()) {
      console.error('Invalid extension ID:', extensionId);
      toast.error('Extension not found');
      return false;
    }
    
    // Validate user ID
    if (!userId?.trim()) {
      console.error('Invalid user ID:', userId);
      toast.error('Please log in to submit a review');
      return false;
    }

    // Validate rating
    if (rating < 1 || rating > 5) {
      console.error('Invalid rating:', rating);
      toast.error('Please select a valid rating (1-5)');
      return false;
    }

    // Validate comment
    if (!comment?.trim()) {
      console.error('Invalid comment:', comment);
      toast.error('Please write a review');
      return false;
    }

    try {
      // Check if user has already reviewed
      const existingReviews = await getDocs(
        query(reviewsCollection,
          where('extensionId', '==', extensionId),
          where('userId', '==', userId)
        )
      );

      if (!existingReviews.empty) {
        console.log('User has already reviewed this extension');
        toast.error('You have already reviewed this extension');
        return false;
      }

      // Add new review using a transaction
      await runTransaction(db, async (transaction) => {
        console.log('Starting transaction');
        
        // First, get the extension document
        const extensionRef = doc(db, 'extensions', extensionId);
        const extensionDoc = await transaction.get(extensionRef);
        
        if (!extensionDoc.exists()) {
          console.error('Extension not found in database');
          throw new Error('Extension not found');
        }

        // Create the review document reference
        const reviewRef = doc(reviewsCollection);
        const reviewData = {
          id: reviewRef.id,
          extensionId,
          userId,
          userDisplayName,
          userPhotoURL: userPhotoURL || '',
          userIcon,
          rating: Math.min(Math.max(rating, 1), 5),
          comment: comment.trim(),
          createdAt: serverTimestamp()
        };

        console.log('Creating review with data:', reviewData);

        // Get current extension stats
        const extensionData = extensionDoc.data();
        const currentTotalRatings = extensionData.totalRatings || 0;
        const currentTotalSum = extensionData.totalRatingSum || 0;

        // Calculate new stats
        const newTotalRatings = currentTotalRatings + 1;
        const newTotalSum = currentTotalSum + reviewData.rating;
        const newAverage = Math.round((newTotalSum / newTotalRatings) * 10) / 10;

        console.log('Updating extension stats:', {
          currentStats: { currentTotalRatings, currentTotalSum },
          newStats: { newTotalRatings, newTotalSum, newAverage }
        });

        // After all reads, perform the writes
        transaction.set(reviewRef, reviewData);
        transaction.update(extensionRef, {
          totalRatings: newTotalRatings,
          totalRatingSum: newTotalSum,
          averageRating: newAverage,
          updatedAt: serverTimestamp()
        });

        console.log('Transaction completed successfully');
      });

      toast.success('Review added successfully');
      return true;
    } catch (error) {
      console.error('Error adding review:', error);
      if (error instanceof Error) {
        toast.error(`Failed to add review: ${error.message}`);
      } else {
        toast.error('Failed to add review');
      }
      return false;
    }
  };

  const deleteReview = async (reviewId: string) => {
    try {
      await runTransaction(db, async (transaction) => {
        // Get the review data first
        const reviewRef = doc(reviewsCollection, reviewId);
        const reviewDoc = await transaction.get(reviewRef);
        
        if (!reviewDoc.exists()) {
          throw new Error('Review not found');
        }

        const reviewData = reviewDoc.data();
        const extensionRef = doc(db, 'extensions', extensionId);
        const extensionDoc = await transaction.get(extensionRef);
        
        if (!extensionDoc.exists()) {
          throw new Error('Extension not found');
        }

        const extensionData = extensionDoc.data();
        const currentTotalRatings = extensionData.totalRatings || 0;
        const currentTotalSum = extensionData.totalRatingSum || 0;

        // Calculate new stats
        const newTotalRatings = Math.max(0, currentTotalRatings - 1);
        const newTotalSum = Math.max(0, currentTotalSum - reviewData.rating);
        const newAverage = newTotalRatings > 0 
          ? Math.round((newTotalSum / newTotalRatings) * 10) / 10 
          : 0;

        // Perform the writes
        transaction.delete(reviewRef);
        transaction.update(extensionRef, {
          totalRatings: newTotalRatings,
          totalRatingSum: newTotalSum,
          averageRating: newAverage,
          updatedAt: serverTimestamp()
        });

        // Create notification after successful transaction
        await addDoc(collection(db, 'notifications'), {
          userId: reviewData.userId,
          type: 'REVIEW_DELETED',
          message: 'Your review has been removed by an administrator.',
          createdAt: serverTimestamp(),
          read: false
        });
      });

      toast.success('Review deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
      return false;
    }
  };

  return { reviews, loading, error, addReview, deleteReview };
}